import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BranchBadge } from './BranchSelector';
import { getStatusBadge, STATUS_MAP } from './dashboardUtils';
import {
  DollarSign, Edit, Trash2, ClipboardList, Shield, AlertTriangle,
  CheckSquare, Timer, Send
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const JobCard = ({ job, can, onQuickProgress, onArchive, onDelete, onPayment, onEdit, onInspect }) => {
  const currentPrice = job.totalPrice ?? job.currentPrice ?? job.total_price ?? job.amount ?? 0;
  const paid = job.paidAmount ?? job.paid_amount ?? 0;
  const remaining = job.remainingBalance ?? (currentPrice - paid);
  const nextStatuses = job.nextStatuses || [];
  const isDelivered = ['teslim_edildi', 'completed'].includes(job.status);
  const isArchived = job.isArchived;
  const canDetail = job.canViewDetail !== false;

  const openJobDetail = () => {
    if (!canDetail) {
      toast({ title: "Erisim Engeli", description: "Bu isin detaylarini gorme yetkiniz yok.", variant: "destructive" });
      return;
    }
    onEdit(job);
  };

  return (
    <div className={`card-premium rounded-2xl p-5 ${isArchived ? 'opacity-60' : ''}`}
      data-testid={`job-card-${job.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className={canDetail ? "cursor-pointer" : "cursor-not-allowed opacity-80"} onClick={openJobDetail}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-mono text-[11px] tracking-wider text-brand">{job.carPlate}</span>
            {job.branchName && (
              <>
                <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                <span className="font-mono text-[11px] text-muted-foreground uppercase">{job.branchName}</span>
              </>
            )}
            {job.createdAt && (
              <>
                <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                <span className="font-mono text-[11px] text-muted-foreground">
                  {new Date(job.createdAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
              </>
            )}
          </div>
          <h3 className={`font-display font-semibold text-lg tracking-tight ${canDetail ? 'hover:text-brand' : ''} transition-colors`}>
            {job.customerName}
          </h3>
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end shrink-0">
          {isArchived && <Badge className="bg-gray-600 text-white text-[10px]">Arsiv</Badge>}
          {job.isDelayed && (
            <Badge className={`text-white text-[10px] ${job.delaySeverity === 'critical' ? 'bg-red-600' : 'bg-yellow-600'}`} data-testid={`delayed-badge-${job.id}`}>
              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{job.delaySeverity === 'critical' ? 'Kritik' : 'Gecikiyor'}
            </Badge>
          )}
          {job.warningCount > 0 && (
            <Badge className="bg-yellow-600 text-white text-[10px]" data-testid={`warning-badge-${job.id}`}>
              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{job.warningCount}
            </Badge>
          )}
          {getStatusBadge(job.status)}
        </div>
      </div>

      {/* Delay info */}
      {job.isDelayed && job.delayReasons?.length > 0 && (
        <div className={`rounded-lg px-3 py-1.5 text-[10px] mb-3 ${job.delaySeverity === 'critical' ? 'bg-red-500/10 text-red-400 hairline' : 'bg-yellow-500/10 text-yellow-400 hairline'}`} data-testid={`delay-info-${job.id}`}>
          {job.delayReasons.join(' | ')}
          {job.stageHours != null && <span className="ml-1 text-muted-foreground">({job.stageHours}s)</span>}
        </div>
      )}

      {/* Services */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {(job.services || []).map((s, i) => (
          <span key={`svc-${s}-${i}`} className="px-2.5 py-1 rounded-md bg-white/5 hairline text-[11px] text-muted-foreground">
            {s}
          </span>
        ))}
      </div>

      {/* Financial grid */}
      {canDetail && (
        <>
          <div className="divider-fade mb-4" />
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[
              ['TUTAR',   currentPrice,              null],
              ['TAHSIL',  paid,                      '#10b981'],
              ['KALAN',   remaining > 0 ? remaining : 0, remaining > 0 ? '#ef4444' : '#10b981'],
              ['ISKONTO', job.discountAmount || 0,   '#eab308'],
            ].map(([label, value, color]) => (
              <div key={label}>
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{label}</div>
                <div className="font-mono text-base font-semibold" style={color ? { color } : {}}>
                  {remaining <= 0 && label === 'KALAN' ? 'Tamam' : Number(value).toLocaleString('tr-TR')}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Quick progress */}
      {can('can_progress_job_status') && nextStatuses.length > 0 && !isArchived && (
        <div className="flex gap-1.5 mb-3" data-testid={`progress-buttons-${job.id}`}>
          {nextStatuses.map(ns => (
            <Button key={ns.key} size="sm" onClick={(e) => { e.stopPropagation(); onQuickProgress(job.id, ns.key); }}
              className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] h-7 flex-1 rounded-lg"
              data-testid={`progress-${job.id}-${ns.key}`}>
              {ns.label}
            </Button>
          ))}
        </div>
      )}

      {/* Checklist + duration */}
      {canDetail && (job.checklistProgress || job.durationHours != null) && (
        <div className="flex items-center gap-2 text-[10px] mb-3">
          {job.checklistProgress && (
            <div className="flex items-center gap-1 flex-1" data-testid={`checklist-bar-${job.id}`}>
              <CheckSquare className="h-3 w-3 text-muted-foreground" />
              <div className="flex-1 bg-white/5 rounded-full h-1.5">
                <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.round((job.checklistProgress.done / Math.max(job.checklistProgress.total, 1)) * 100)}%` }} />
              </div>
              <span className="text-muted-foreground">{job.checklistProgress.done}/{job.checklistProgress.total}</span>
            </div>
          )}
          {job.durationHours != null && (
            <span className="text-muted-foreground flex items-center gap-0.5"><Timer className="h-3 w-3" />{job.durationHours}s</span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-1.5 flex-wrap pt-3 hairline-t">
        {canDetail && can('can_enter_payments') && !job.isPaid && !isArchived && (
          <Button size="sm" onClick={() => onPayment(job)}
            className="cta-brand text-xs h-7 rounded-lg" data-testid={`add-payment-${job.id}`}>
            <DollarSign className="h-3 w-3 mr-1" />Odeme
          </Button>
        )}
        {canDetail && can('can_edit_jobs') && !isArchived && (
          <Button size="sm" variant="outline" onClick={openJobDetail}
            className="hairline text-muted-foreground text-xs h-7 rounded-lg hover:text-foreground hover:bg-white/5" data-testid={`edit-job-${job.id}`}>
            <Edit className="h-3 w-3" />
          </Button>
        )}
        {canDetail && can('can_upload_photos') && !isArchived && (
          <Button size="sm" variant="outline" onClick={() => onInspect(job)}
            className="hairline text-muted-foreground text-xs h-7 rounded-lg hover:text-foreground hover:bg-white/5" data-testid={`inspect-job-${job.id}`}>
            <ClipboardList className="h-3 w-3" />
          </Button>
        )}
        {can('can_archive_jobs') && isDelivered && !isArchived && (
          <Button size="sm" variant="outline" onClick={() => onArchive(job.id)}
            className="border-yellow-700/30 text-yellow-400 text-xs h-7 rounded-lg hover:bg-yellow-500/10" data-testid={`archive-job-${job.id}`}>
            <Shield className="h-3 w-3 mr-1" />Arsivle
          </Button>
        )}
        {can('can_delete_jobs') && !isDelivered && !isArchived && (
          <Button size="sm" variant="outline" onClick={() => onDelete(job.id)}
            className="border-red-800/30 text-red-400 text-xs h-7 rounded-lg hover:bg-red-500/10" data-testid={`delete-job-${job.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default JobCard;
